import { z } from "zod";

/**
 * Fase 3b · la tinta de una hoja llena: lo que se escribe con el lápiz encima del formato, como
 * en GoodNotes. Se guardan los trazos como vectores (no una imagen), así se redibujan nítidos a
 * cualquier escala y el PDF de la hoja puede estamparlos después.
 *
 * Las coordenadas son píxeles CSS de la hoja carta a tamaño real (vertical: 216 mm de ancho =
 * 816,4 px; horizontal: 279,4 mm = 1056 px), origen arriba a la izquierda de la hoja, no de la
 * pantalla: la tinta cae en el mismo lugar del formato en la tablet, en el teléfono y en el
 * escritorio.
 */

/** Ancho de la hoja carta vertical en píxeles CSS (216 mm a 96 dpi). */
export const ANCHO_HOJA_PX = (216 / 25.4) * 96;
/** Ancho de la hoja carta horizontal (279,4 mm). */
export const ANCHO_HOJA_HORIZONTAL_PX = (279.4 / 25.4) * 96;

const HERRAMIENTAS_DE_TINTA = ["lapiz", "resaltador"] as const;

/** Pocos colores y con nombre: la hoja es evidencia, no un dibujo. */
export const COLORES_DE_TINTA = {
  negro: "#1b1c20",
  azul: "#1d4ed8",
  rojo: "#c62828",
  verde: "#2e7d32",
  amarillo: "#f5d000",
} as const;
export type ColorDeTinta = keyof typeof COLORES_DE_TINTA;
const NOMBRES_DE_COLOR = Object.keys(COLORES_DE_TINTA) as [ColorDeTinta, ...ColorDeTinta[]];

/** Tope de puntos por trazo: un trazo larguísimo sigue en ~4000 muestras. */
export const MAX_PUNTOS_POR_TRAZO = 4000;

const coordenada = z.number().min(-2000).max(200_000);

const trazo = z.object({
  id: z.string().min(1).max(24),
  herramienta: z.enum(HERRAMIENTAS_DE_TINTA),
  color: z.enum(NOMBRES_DE_COLOR),
  /** Grosor base en píxeles de la hoja. */
  grosor: z.number().min(0.5).max(40),
  /** Si la presión es la del lápiz; si no (dedo, ratón), se simula por la velocidad. */
  presion: z.boolean(),
  /**
   * El día (AAAA-MM-DD, Honduras) en que se escribió. Lo pone el servidor al guardar, no la
   * pantalla: lo escrito un día queda fijo los días siguientes (`sellarTinta`).
   */
  dia: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  /** Plano: x, y, presión (0–1), x, y, presión… */
  puntos: z
    .array(coordenada)
    .min(3)
    .max(MAX_PUNTOS_POR_TRAZO * 3)
    .refine((p) => p.length % 3 === 0, "Los puntos van de tres en tres: x, y, presión"),
});
export type Trazo = z.infer<typeof trazo>;

export const esquemaTinta = z.array(trazo).max(5000);
export type Tinta = Trazo[];

/**
 * Cómo quedó una hoja llena, lo que dice quien la llenó al terminar (la tinta no se puede leer
 * como datos, así que el filtro del auditor sale de acá): todo aceptable, o hubo no aceptables y
 * queda pendiente la verificación en el sitio, que la misma persona escribe otro día.
 */
export const RESULTADOS_DE_HOJA = ["aceptable", "no_aceptable"] as const;
export type ResultadoDeHoja = (typeof RESULTADOS_DE_HOJA)[number];
export const NOMBRE_RESULTADO: Record<ResultadoDeHoja, string> = {
  aceptable: "Todo aceptable",
  no_aceptable: "Hubo no aceptables",
};

/** abierta: sin resultado · por_verificar: hubo no aceptables · cerrada: nada pendiente. */
export const ESTADOS_DE_HOJA = ["abierta", "por_verificar", "cerrada"] as const;
export type EstadoDeHoja = (typeof ESTADOS_DE_HOJA)[number];
export const NOMBRE_ESTADO_HOJA: Record<EstadoDeHoja, string> = {
  abierta: "Abierta",
  por_verificar: "Por verificar",
  cerrada: "Cerrada",
};

/** Los trazos de días anteriores a `hoy`: los que ya no se borran ni se cambian. */
export function trazosFijos(tinta: Tinta, opciones: { hoy: string; diaDeLaHoja: string }): Trazo[] {
  return tinta.filter((t) => (t.dia ?? opciones.diaDeLaHoja) < opciones.hoy);
}

/**
 * El guardado de una hoja visto por el servidor: los trazos nuevos se sellan con el día de hoy,
 * los que ya estaban conservan su día (lo que diga la pantalla no cuenta), y lo escrito en días
 * anteriores tiene que seguir ahí, idéntico. Así la verificación de otro día se suma a la hoja
 * sin tocar la inspección. Un trazo sin día es de antes de esta regla: cuenta como del día de
 * la hoja.
 */
export function sellarTinta(
  anterior: Tinta,
  nueva: Tinta,
  opciones: { hoy: string; diaDeLaHoja: string },
): { ok: true; tinta: Tinta } | { ok: false; mensaje: string } {
  const previos = new Map(anterior.map((t) => [t.id, t]));
  const ids = new Set(nueva.map((t) => t.id));
  for (const fijo of trazosFijos(anterior, opciones)) {
    const ahora = nueva.find((t) => t.id === fijo.id);
    if (!ahora || !mismoTrazo(fijo, ahora)) {
      return { ok: false, mensaje: "Lo escrito otro día no se borra ni se cambia" };
    }
  }
  if (ids.size !== nueva.length) return { ok: false, mensaje: "Hay trazos repetidos" };
  return {
    ok: true,
    tinta: nueva.map((t) => {
      const previo = previos.get(t.id);
      return { ...t, dia: previo ? (previo.dia ?? opciones.diaDeLaHoja) : opciones.hoy };
    }),
  };
}

function mismoTrazo(a: Trazo, b: Trazo): boolean {
  return (
    a.herramienta === b.herramienta &&
    a.color === b.color &&
    a.grosor === b.grosor &&
    a.presion === b.presion &&
    a.puntos.length === b.puntos.length &&
    a.puntos.every((v, i) => v === b.puntos[i])
  );
}

// ---------------------------------------------------------------------------------------------
// Formatos subidos como PDF: la tinta va encima de las páginas del archivo
// ---------------------------------------------------------------------------------------------

/** Píxeles CSS por punto PDF (96 dpi sobre 72). */
export const PX_POR_PT = 96 / 72;
/** El blanco entre una página y la siguiente en pantalla; lo que se escriba ahí no se imprime. */
const SEPARACION_DE_PAGINAS_PX = 16;

/** Una página del PDF ya derecha (con su giro aplicado), en puntos. */
export type TamanoDePagina = { ancho: number; alto: number };

/**
 * Cómo se apilan las páginas de un formato PDF, en píxeles de la hoja: una debajo de otra,
 * alineadas a la izquierda, con `SEPARACION_DE_PAGINAS_PX` entre ellas. Es el sistema de
 * coordenadas de la tinta de esas hojas: la pantalla lo usa para dibujar y el worker para
 * devolver cada trazo a su página. Los dos lo calculan con esta misma función.
 */
export function disposicionDePaginas(paginas: TamanoDePagina[]): {
  ancho: number;
  alto: number;
  paginas: { arriba: number; ancho: number; alto: number }[];
} {
  let arriba = 0;
  const salida = paginas.map((p, i) => {
    const pagina = { arriba, ancho: p.ancho * PX_POR_PT, alto: p.alto * PX_POR_PT };
    arriba += pagina.alto + (i < paginas.length - 1 ? SEPARACION_DE_PAGINAS_PX : 0);
    return pagina;
  });
  return { ancho: Math.max(0, ...salida.map((p) => p.ancho)), alto: arriba, paginas: salida };
}
